import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/dashboard/DashboardIcons';
import { API, auth, fmtDate, PLAN_STATUS_LABELS, SESSION_STATUS_LABELS, StudioEmpty } from './shared';

export default function StudioDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/coach-studio/dashboard`, auth());
        setData(res.data);
      } catch (e) {
        setError('تعذر تحميل لوحة العمل الفني');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="p-6 text-center text-sm text-slate-400" data-testid="studio-dashboard-loading">جاري التحميل...</div>;
  if (error) return <div className="p-6 text-center text-sm text-red-500" data-testid="studio-dashboard-error">{error}</div>;

  return (
    <div className="space-y-6" data-testid="studio-dashboard-page">
      <div className="flex flex-wrap gap-3">
        <QuickAction label="تمرين جديد" icon="dumbbell" onClick={() => navigate('/coach/studio/exercises/new')} testId="quick-new-exercise" />
        <QuickAction label="خطة جديدة" icon="clipboard" onClick={() => navigate('/coach/studio/plans?new=1')} testId="quick-new-plan" />
        <QuickAction label="جلسة جديدة" icon="calendar" onClick={() => navigate('/coach/studio/schedule?new=1')} testId="quick-new-session" />
        <QuickAction label="فتح الملعب" icon="field" onClick={() => navigate('/coach/studio/exercises/new?pitch=1')} testId="quick-open-pitch" />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="جلسة اليوم" testId="dashboard-today-sessions">
          {data.today_sessions.length === 0 ? <StudioEmpty text="لا توجد جلسة اليوم" icon={<Icon name="calendar" className="w-5 h-5" />} /> :
            data.today_sessions.map((s) => <SessionRow key={s.id} s={s} navigate={navigate} />)}
        </Card>
        <Card title="الجلسات القادمة" testId="dashboard-upcoming-sessions">
          {data.upcoming_sessions.length === 0 ? <StudioEmpty text="لا توجد جلسات قادمة" icon={<Icon name="calendar" className="w-5 h-5" />} /> :
            data.upcoming_sessions.map((s) => <SessionRow key={s.id} s={s} navigate={navigate} />)}
        </Card>
        <Card title="الخطط النشطة" testId="dashboard-active-plans">
          {data.active_plans.length === 0 ? <StudioEmpty text="لا توجد خطط نشطة" icon={<Icon name="clipboard" className="w-5 h-5" />} /> :
            data.active_plans.map((p) => (
              <div key={p.id} className="flex items-center justify-between border-b border-slate-100 py-2 text-sm cursor-pointer" onClick={() => navigate(`/coach/studio/plans/${p.id}`)}>
                <span className="text-slate-700">{p.name}</span>
                <span className="text-xs text-slate-400">{PLAN_STATUS_LABELS[p.status]}</span>
              </div>
            ))}
          {data.active_plan_completion_percent !== null && data.active_plan_completion_percent !== undefined && (
            <div className="mt-2 text-xs text-slate-500">نسبة تنفيذ الخطة الحالية: {data.active_plan_completion_percent}٪</div>
          )}
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="آخر التمارين" testId="dashboard-recent-exercises">
          {data.recent_exercises.length === 0 ? <StudioEmpty text="لا توجد تمارين بعد" icon={<Icon name="dumbbell" className="w-5 h-5" />} /> :
            data.recent_exercises.map((e) => (
              <div key={e.id} className="cursor-pointer border-b border-slate-100 py-2 text-sm text-slate-700" onClick={() => navigate(`/coach/studio/exercises/${e.id}`)}>{e.title}</div>
            ))}
        </Card>
        <Card title="التمارين المفضلة" testId="dashboard-favorite-exercises">
          {data.favorite_exercises.length === 0 ? <StudioEmpty text="لا توجد تمارين مفضلة" icon={<Icon name="star" className="w-5 h-5" />} /> :
            data.favorite_exercises.map((e) => (
              <div key={e.id} className="cursor-pointer border-b border-slate-100 py-2 text-sm text-slate-700" onClick={() => navigate(`/coach/studio/exercises/${e.id}`)}>{e.title}</div>
            ))}
        </Card>
        <Card title="نشاط المشاركة" testId="dashboard-sharing-activity">
          <div className="flex items-center justify-between py-2 text-sm"><span className="text-slate-600">محتوى مشارك معي</span><span className="font-bold text-slate-900">{data.shared_with_me_count}</span></div>
          <div className="flex items-center justify-between py-2 text-sm"><span className="text-slate-600">تحديثات اعتماد بانتظار المراجعة</span><span className="font-bold text-slate-900">{data.pending_review_notes}</span></div>
        </Card>
      </div>
    </div>
  );
}

const QuickAction = ({ label, icon, onClick, testId }) => (
  <button onClick={onClick} data-testid={testId} className="flex items-center gap-2 rounded-2xl border border-slate-200/70 bg-white px-4 py-3.5 text-sm font-bold text-slate-700 shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-all active:scale-95 hover:border-teal-300 hover:bg-teal-50">
    <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-teal-50 to-emerald-50"><Icon name={icon} className="w-4 h-4 text-teal-600" /></span> {label}
  </button>
);

const Card = ({ title, children, testId }) => (
  <div className="rounded-2xl border border-slate-200/70 bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)] sm:p-5" data-testid={testId}>
    <h3 className="mb-3 text-sm font-bold text-slate-900">{title}</h3>
    {children}
  </div>
);

const SessionRow = ({ s, navigate }) => (
  <div className="flex items-center justify-between border-b border-slate-100 py-2 text-sm cursor-pointer" onClick={() => navigate('/coach/studio/schedule')}>
    <span className="text-slate-700">{s.title}</span>
    <span className="text-xs text-slate-400">{fmtDate(s.date)} {s.start_time || ''}</span>
  </div>
);
